import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Edit, Trash2, Car, Fuel, Gauge, ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { getWorkers } from '../../../redux/features/workerSlice';

const formatMoney = (value) =>
  new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('sr-RS');
};

const kmTypeLabel = (type) => {
  if (type === 'start') return 'Početno';
  if (type === 'end') return 'Završno';
  return 'Ažurirano';
};

const totalFuelFrom = (vehicle) => {
  if (Number(vehicle.totalFuel) > 0) return Number(vehicle.totalFuel);
  return (vehicle.fuelRecords || []).reduce((sum, record) => sum + (Number(record.amount) || 0), 0);
};

const totalKmFrom = (vehicle) => {
  if (Number(vehicle.totalKilometers) > 0) return Number(vehicle.totalKilometers);
  const records = [...(vehicle.kilometerRecords || [])].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );
  return records[0] ? Number(records[0].kilometers) || 0 : 0;
};

const driverIdValue = (driverId) => {
  if (!driverId) return '';
  if (typeof driverId === 'object') return driverId._id || driverId.id || '';
  return driverId;
};

const AdminVehiclesView = () => {
  const dispatch = useDispatch();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState({
    registrationNumber: '',
    model: '',
    driverId: ''
  });
  const { workers } = useSelector((state) => state.worker || { workers: [] });

  useEffect(() => {
    dispatch(getWorkers());
    fetchVehicles();
  }, [dispatch]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem('profile') || '{}')?.token;
      const response = await axios.get(`${API_BASE_URL}/api/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Greška pri učitavanju vozila');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = JSON.parse(localStorage.getItem('profile') || '{}')?.token;
      if (editingVehicle) {
        await axios.put(
          `${API_BASE_URL}/api/vehicles/${editingVehicle._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Vozilo ažurirano');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/vehicles`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Vozilo dodato');
      }
      setShowForm(false);
      setEditingVehicle(null);
      setFormData({ registrationNumber: '', model: '', driverId: '' });
      fetchVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error(error.response?.data?.message || 'Greška pri čuvanju vozila');
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      registrationNumber: vehicle.registrationNumber || '',
      model: vehicle.model || '',
      driverId: driverIdValue(vehicle.driverId)
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovo vozilo?')) return;
    try {
      const token = JSON.parse(localStorage.getItem('profile') || '{}')?.token;
      await axios.delete(`${API_BASE_URL}/api/vehicles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vozilo obrisano');
      fetchVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Greška pri brisanju vozila');
    }
  };

  const getDriverName = (driverId) => {
    if (driverId && typeof driverId === 'object') {
      const name = `${driverId.firstName || ''} ${driverId.lastName || ''}`.trim();
      return name || 'N/A';
    }
    const driver = workers.find((w) => String(w._id) === String(driverId));
    return driver ? `${driver.firstName} ${driver.lastName}` : 'N/A';
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Službena vozila</h1>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingVehicle(null);
            setFormData({ registrationNumber: '', model: '', driverId: '' });
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
        >
          <Plus className="h-4 w-4" />
          Dodaj vozilo
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4">
            {editingVehicle ? 'Izmeni vozilo' : 'Dodaj novo vozilo'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Registarska oznaka
              </label>
              <input
                type="text"
                value={formData.registrationNumber}
                onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Model
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Vozač
              </label>
              <select
                value={formData.driverId}
                onChange={(e) => setFormData({ ...formData, driverId: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                required
              >
                <option value="">Izaberi vozača</option>
                {workers.map((worker) => (
                  <option key={worker._id} value={worker._id}>
                    {worker.firstName} {worker.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                {editingVehicle ? 'Sačuvaj izmene' : 'Dodaj'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingVehicle(null);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
              >
                Otkaži
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-400">Učitavanje...</div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-8 text-gray-400">Nema vozila</div>
      ) : (
        <div className="space-y-4">
          {vehicles.map((vehicle) => {
            const fuelRecords = [...(vehicle.fuelRecords || [])].sort(
              (a, b) => new Date(b.date) - new Date(a.date)
            );
            const kmRecords = [...(vehicle.kilometerRecords || [])].sort(
              (a, b) => new Date(b.date) - new Date(a.date) || String(b.time || '').localeCompare(String(a.time || ''))
            );
            const isOpen = expandedId === vehicle._id;
            const fuelCost = (vehicle.fuelRecords || []).reduce(
              (sum, record) => sum + (Number(record.price) || 0),
              0
            );

            return (
              <div key={vehicle._id} className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : vehicle._id)}
                    className="flex-1 text-left min-w-0"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Car className="h-5 w-5 text-blue-400 shrink-0" />
                      <h3 className="text-lg font-semibold text-white truncate">
                        {vehicle.registrationNumber}
                      </h3>
                      <ChevronDown className={`h-4 w-4 text-gray-400 shrink-0 transition ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                    <div className="space-y-1 text-sm text-gray-300">
                      <div><span className="font-medium">Model:</span> {vehicle.model}</div>
                      <div><span className="font-medium">Vozač:</span> {getDriverName(vehicle.driverId)}</div>
                      <div className="flex flex-wrap items-center gap-4 mt-2 pt-2 border-t border-gray-700">
                        <span className="inline-flex items-center gap-1.5">
                          <Fuel className="h-4 w-4 text-green-400" />
                          Sipano gorivo: {totalFuelFrom(vehicle)} L
                          {fuelCost > 0 ? ` · ${formatMoney(fuelCost)}` : ''}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Gauge className="h-4 w-4 text-yellow-400" />
                          Kilometraža: {totalKmFrom(vehicle)} km
                        </span>
                      </div>
                    </div>
                  </button>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => handleEdit(vehicle)}
                      className="p-1 text-blue-400 hover:text-blue-300"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(vehicle._id)}
                      className="p-1 text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-700 pt-4">
                    <div>
                      <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-1.5">
                        <Fuel className="h-4 w-4 text-green-400" />
                        Gorivo
                      </h4>
                      {fuelRecords.length === 0 ? (
                        <p className="text-xs text-gray-500">Nema unosa goriva</p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {fuelRecords.map((record) => (
                            <div key={record._id || `${record.date}-${record.amount}`} className="bg-gray-700/70 rounded p-2.5 flex justify-between gap-2">
                              <div>
                                <p className="text-sm text-white">{record.amount} L</p>
                                <p className="text-xs text-gray-400">{formatDate(record.date)}</p>
                              </div>
                              <p className="text-sm text-white">{formatMoney(record.price)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-1.5">
                        <Gauge className="h-4 w-4 text-yellow-400" />
                        Kilometraža
                      </h4>
                      {kmRecords.length === 0 ? (
                        <p className="text-xs text-gray-500">Nema unosa kilometraže</p>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {kmRecords.map((record) => (
                            <div key={record._id || `${record.date}-${record.time}-${record.kilometers}`} className="bg-gray-700/70 rounded p-2.5">
                              <div className="flex justify-between gap-2">
                                <p className="text-sm text-white">{record.kilometers} km</p>
                                <p className="text-xs text-gray-400">{kmTypeLabel(record.type)}</p>
                              </div>
                              <p className="text-xs text-gray-400">
                                {formatDate(record.date)}{record.time ? ` · ${record.time}` : ''}
                              </p>
                              {record.notes ? <p className="text-xs text-gray-500 mt-1">{record.notes}</p> : null}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminVehiclesView;
